import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import logger from '%app/js/common/services/Logger';
import { readErrorKind } from './fetchJsonResource';
import { resolveRetryDelayMs, shouldRetry } from './rateLimitRetryPolicy';

function buildIdleState(initialData) {
    return { data: initialData, status: 'idle', errorKind: null };
}

function buildLoadingState(initialData) {
    return { data: initialData, status: 'loading', errorKind: null };
}

function buildSuccessState(data) {
    return { data, status: 'success', errorKind: null };
}

function buildPendingState(initialData, key) {
    return key ? buildLoadingState(initialData) : buildIdleState(initialData);
}

function toCachedState(entry) {
    return entry === null ? null : buildSuccessState(entry.data);
}

function peekCachedState(cache, key) {
    return !cache || !key ? null : toCachedState(cache.peek(key));
}

function readCachedState(cache, key) {
    return !cache || !key ? null : toCachedState(cache.read(key));
}

function resolveVisibleState(cache, key, publishedKey, publishedState, initialData) {
    if (publishedKey === key) {
        return publishedState;
    }

    return peekCachedState(cache, key) ?? buildPendingState(initialData, key);
}

function useResource(fetchFn, key, { initialData = null, cache = null } = {}) {
    const initialDataRef = useRef(initialData);
    initialDataRef.current = initialData;
    const fetchFnRef = useRef(fetchFn);
    fetchFnRef.current = fetchFn;
    const cacheRef = useRef(cache);
    cacheRef.current = cache;
    const [state, setState] = useState(() => (
        peekCachedState(cache, key) ?? buildIdleState(initialDataRef.current)
    ));
    const controllerRef = useRef(null);
    const attemptRef = useRef(0);
    const retryTimerRef = useRef(null);
    const stateRef = useRef(state);
    const stateKeyRef = useRef(key);

    const publishState = useCallback((nextState, publishedKey) => {
        stateRef.current = nextState;
        stateKeyRef.current = publishedKey;
        setState(nextState);
    }, []);

    const clearRetryTimer = useCallback(() => {
        if (retryTimerRef.current !== null) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
    }, []);

    const abortInFlightRequest = useCallback(() => {
        clearRetryTimer();

        if (controllerRef.current) {
            controllerRef.current.abort();
            controllerRef.current = null;
        }

        attemptRef.current += 1;
    }, [clearRetryTimer]);

    const fetchFromNetwork = useCallback(() => {
        const currentInitialData = initialDataRef.current;
        const controller = new AbortController();
        controllerRef.current = controller;
        const attemptId = attemptRef.current;

        publishState(buildLoadingState(currentInitialData), key);

        const attempt = (attemptsMade) => {
            fetchFnRef.current(key, { signal: controller.signal })
                .then((data) => {
                    if (attemptId !== attemptRef.current) {
                        return;
                    }

                    if (cacheRef.current) {
                        cacheRef.current.write(key, data);
                    }

                    publishState(buildSuccessState(data), key);
                })
                .catch((error) => {
                    if (error.name === 'AbortError') {
                        return;
                    }

                    if (attemptId !== attemptRef.current) {
                        return;
                    }

                    if (shouldRetry(error, attemptsMade)) {
                        retryTimerRef.current = setTimeout(() => {
                            retryTimerRef.current = null;

                            if (attemptId === attemptRef.current) {
                                attempt(attemptsMade + 1);
                            }
                        }, resolveRetryDelayMs(error, attemptsMade));

                        return;
                    }

                    logger.warning('useResource: failed to load resource', { error });
                    publishState({ data: currentInitialData, status: 'error', errorKind: readErrorKind(error) }, key);
                });
        };

        attempt(0);
    }, [key, publishState]);

    const loadServingCachedData = useCallback(() => {
        if (!key) {
            publishState(buildIdleState(initialDataRef.current), key);
            return;
        }

        abortInFlightRequest();

        const cachedState = readCachedState(cacheRef.current, key);

        if (cachedState !== null) {
            publishState(cachedState, key);
            return;
        }

        fetchFromNetwork();
    }, [key, publishState, abortInFlightRequest, fetchFromNetwork]);

    const loadBypassingCache = useCallback(() => {
        if (!key) {
            publishState(buildIdleState(initialDataRef.current), key);
            return;
        }

        abortInFlightRequest();
        fetchFromNetwork();
    }, [key, publishState, abortInFlightRequest, fetchFromNetwork]);

    useEffect(() => {
        loadServingCachedData();

        return abortInFlightRequest;
    }, [loadServingCachedData, abortInFlightRequest]);

    const setData = useCallback((updater) => {
        abortInFlightRequest();

        const currentlyVisibleState = resolveVisibleState(
            cacheRef.current,
            key,
            stateKeyRef.current,
            stateRef.current,
            initialDataRef.current,
        );
        const baseData = currentlyVisibleState.data ?? initialDataRef.current;
        const nextData = typeof updater === 'function' ? updater(baseData) : updater;

        publishState(buildSuccessState(nextData), key);
    }, [key, publishState, abortInFlightRequest]);

    const visibleState = resolveVisibleState(cache, key, stateKeyRef.current, state, initialDataRef.current);

    return {
        ...visibleState,
        retry: loadBypassingCache,
        setData,
    };
}

export default useResource;
